import { useEffect, useRef } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Text } from '../../atoms/Typography/Text';
import { useTranslation } from '../../../i18n/useTranslation';
import './SyncLog.css';

export type SyncLogLevel = 'info' | 'success' | 'warning' | 'error';

export interface SyncLogEntry {
  id: string;
  level: SyncLogLevel;
  message: string;
  detail?: string;
  /** Technical detail for support — rendered collapsed behind a disclosure. */
  technicalDetail?: string;
  timestamp: Date;
}

interface SyncLogProps {
  entries: SyncLogEntry[];
  isRunning?: boolean;
  onClear?: () => void;
  className?: string;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function LevelIndicator({ level }: { level: SyncLogLevel }) {
  return (
    <span className={`sync-log__indicator sync-log__indicator--${level}`} aria-hidden="true" />
  );
}

function RunningPulse() {
  return (
    <span className="sync-log__pulse" aria-hidden="true">
      <span className="sync-log__pulse-dot" />
    </span>
  );
}

export function SyncLog({ entries, isRunning = false, onClear, className }: SyncLogProps) {
  const { t } = useTranslation();
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [entries]);

  const isEmpty = entries.length === 0 && !isRunning;

  return (
    <section
      className={['sync-log', className].filter(Boolean).join(' ')}
      aria-label={t('integrations.faunaMarin.log.title')}
      aria-live="polite"
      aria-relevant="additions"
    >
      <header className="sync-log__header">
        <div className="sync-log__header-left">
          <span className="sync-log__title">{t('integrations.faunaMarin.log.title')}</span>
          {isRunning && <RunningPulse />}
        </div>
        {entries.length > 0 && !isRunning && onClear && (
          <Button
            variant="ghost"
            className="sync-log__clear-btn"
            onClick={onClear}
            aria-label={t('integrations.faunaMarin.log.clearButton')}
          >
            {t('integrations.faunaMarin.log.clearButton')}
          </Button>
        )}
      </header>

      <div className="sync-log__body" ref={bodyRef}>
        {isEmpty ? (
          <Text className="sync-log__empty">{t('integrations.faunaMarin.log.empty')}</Text>
        ) : (
          <ol className="sync-log__entries" aria-label={t('integrations.faunaMarin.log.title')}>
            {entries.map((entry) => (
              <li key={entry.id} className={`sync-log__entry sync-log__entry--${entry.level}`}>
                <LevelIndicator level={entry.level} />
                <div className="sync-log__entry-content">
                  <div className="sync-log__entry-main">
                    <time
                      className="sync-log__entry-time"
                      dateTime={entry.timestamp.toISOString()}
                    >
                      {formatTime(entry.timestamp)}
                    </time>
                    <span className="sync-log__entry-message">{entry.message}</span>
                  </div>
                  {entry.detail && (
                    <Text className="sync-log__entry-detail">{entry.detail}</Text>
                  )}
                  {entry.technicalDetail && (
                    <details className="sync-log__entry-technical">
                      <summary className="sync-log__entry-technical-summary">
                        {t('integrations.faunaMarin.log.technicalDetails')}
                      </summary>
                      <Text className="sync-log__entry-detail">{entry.technicalDetail}</Text>
                    </details>
                  )}
                </div>
              </li>
            ))}
            {isRunning && (
              <li className="sync-log__entry sync-log__entry--running" aria-hidden="true">
                <span className="sync-log__indicator sync-log__indicator--running" />
                <div className="sync-log__entry-content">
                  <div className="sync-log__entry-main">
                    <span className="sync-log__entry-time">&nbsp;</span>
                    <span className="sync-log__entry-message sync-log__cursor" />
                  </div>
                </div>
              </li>
            )}
          </ol>
        )}
      </div>
    </section>
  );
}
